/**
 * Password validation utility
 * Enforces complex password policy:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */

/**
 * Validates password against complex policy
 * @param {string} password - The password to validate
 * @returns {object} - { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
  const errors = [];

  if (!password) {
    return { isValid: false, errors: ['Password is required'] };
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * React Hook Form validation function for password field
 * @param {string} value - The password value
 * @returns {boolean|string} - true if valid, error message if invalid
 */
export const passwordValidationRule = (value) => {
  const { isValid, errors } = validatePassword(value);
  
  if (isValid) {
    return true;
  }
  
  // Return the first error message
  return errors[0] || 'Password does not meet requirements';
};

/**
 * Regex pattern for password validation
 * Matches: at least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
 */
export const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

/**
 * Get password strength indicator
 * @param {string} password - The password to check
 * @returns {object} - { strength: 'weak'|'medium'|'strong', score: number }
 */
export const getPasswordStrength = (password) => {
  if (!password) {
    return { strength: 'weak', score: 0 };
  }

  let score = 0;
  
  // Length check
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1;
  
  // Determine strength
  let strength = 'weak';
  if (score >= 5) strength = 'strong';
  else if (score >= 3) strength = 'medium';
  
  return { strength, score };
};

